import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaCloudUploadAlt,
  FaChevronDown,
  FaChevronRight,
  FaCopy,
  FaDownload,
  FaEdit,
  FaFileAlt,
  FaFolder,
  FaFolderOpen,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import styled, { keyframes } from "styled-components";
import Swal from "sweetalert2";
import { apiUrl } from "../../api/config";
import { getAssetPublicUrl } from "../../api/assets";
import { getStoredAuthToken } from "../../authSession";
import { getClipboardFiles, isVisiblePasteRoot } from "../../shared/clipboardFiles";
import { confirmAssetRelocation } from "../../features/assets/relocation";

const getAuthHeaders = (extra = {}) => {
  const token = getStoredAuthToken();
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

const normalizePrefix = (value) => {
  const text = String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/{2,}/g, "/");
  return text && !text.endsWith("/") ? `${text}/` : text;
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/{2,}/g, "/");

const getFilename = (key) => key.split("/").pop() || key;
const getPublicUrl = getAssetPublicUrl;
const getOfficeViewerUrl = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
const isFolderMarkerKey = (key) => {
  const normalized = String(key || "");
  return normalized.endsWith("/.keep") || normalized.endsWith("/");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getFileExtension = (key) => {
  const filename = getFilename(key).toLowerCase();
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex >= 0 ? filename.slice(dotIndex + 1) : "";
};

const getPreviewType = (key) => {
  const extension = getFileExtension(key);
  if (["avif", "gif", "jpg", "jpeg", "png", "svg", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") return "pdf";
  if (["doc", "docx"].includes(extension)) return "word";
  if (["mp4", "mov", "m4v", "webm"].includes(extension)) return "video";
  if (["aac", "m4a", "mp3", "ogg", "wav"].includes(extension)) return "audio";
  return "file";
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("zh-TW", { hour12: false });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const readAllDirectoryEntries = (reader) =>
  new Promise((resolve, reject) => {
    const entries = [];
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (!batch.length) {
            resolve(entries);
            return;
          }
          entries.push(...batch);
          readBatch();
        },
        reject,
      );
    };
    readBatch();
  });

const getFileFromEntry = (entry) =>
  new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });

const collectDroppedEntryFiles = async (entry, parentPath = "") => {
  if (!entry) return [];
  const entryPath = `${parentPath}${entry.name}`;
  if (entry.isFile) {
    const file = await getFileFromEntry(entry);
    return [{ file, relativePath: entryPath }];
  }
  if (!entry.isDirectory) return [];

  const entries = await readAllDirectoryEntries(entry.createReader());
  const nestedFiles = await Promise.all(
    entries.map((childEntry) => collectDroppedEntryFiles(childEntry, `${entryPath}/`)),
  );
  return nestedFiles.flat();
};

const collectDroppedFiles = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const entryItems = items
    .map((item) => item.webkitGetAsEntry?.())
    .filter(Boolean);
  if (entryItems.length) {
    const droppedFiles = await Promise.all(
      entryItems.map((entry) => collectDroppedEntryFiles(entry)),
    );
    return droppedFiles.flat();
  }

  return Array.from(dataTransfer?.files || []).map((file) => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
};

const buildTree = (files) => {
  const root = {
    name: "全部檔案",
    prefix: "",
    children: new Map(),
    count: files.filter((file) => !isFolderMarkerKey(file.key)).length,
  };
  files.forEach((file) => {
    const isMarker = isFolderMarkerKey(file.key);
    const parts = file.key.split("/").filter(Boolean);
    const treeParts = String(file.key || "").endsWith("/") ? [...parts, ".keep"] : parts;
    let node = root;
    treeParts.slice(0, -1).forEach((part, index) => {
      const prefix = `${treeParts.slice(0, index + 1).join("/")}/`;
      if (!node.children.has(part)) {
        node.children.set(part, { name: part, prefix, children: new Map(), count: 0 });
      }
      node = node.children.get(part);
      if (!isMarker) node.count += 1;
    });
  });
  return root;
};

const flattenTree = (node, expandedPrefixes, level = 0) => {
  const children = Array.from(node.children.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "zh-Hant"),
  );
  const expanded = !node.prefix || expandedPrefixes.has(node.prefix);
  return [
    {
      name: node.name,
      prefix: node.prefix,
      count: node.count,
      level,
      hasChildren: children.length > 0,
      expanded,
    },
    ...(expanded
      ? children.flatMap((child) => flattenTree(child, expandedPrefixes, level + 1))
      : []),
  ];
};

const getPrefixAncestors = (prefix) => {
  const parts = String(prefix || "").split("/").filter(Boolean);
  return parts.map((_, index) => `${parts.slice(0, index + 1).join("/")}/`);
};

const getExpandablePrefixes = (node) => {
  const children = Array.from(node.children.values());
  return [
    ...(node.prefix && children.length ? [node.prefix] : []),
    ...children.flatMap((child) => getExpandablePrefixes(child)),
  ];
};

const getImmediateFolders = (files, currentPrefix) => {
  const folders = new Map();
  files.forEach((file) => {
    const isMarker = isFolderMarkerKey(file.key);
    if (currentPrefix && !file.key.startsWith(currentPrefix)) return;
    const relativeKey = currentPrefix ? file.key.slice(currentPrefix.length) : file.key;
    const [folderName] = relativeKey.split("/");
    if (!folderName || folderName === relativeKey) return;
    const prefix = `${currentPrefix}${folderName}/`;
    const existingFolder = folders.get(prefix);
    folders.set(prefix, {
      name: folderName,
      prefix,
      count: (existingFolder?.count || 0) + (isMarker ? 0 : 1),
    });
  });
  return Array.from(folders.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "zh-Hant"),
  );
};

const getBreadcrumbs = (prefix) => {
  const parts = prefix.split("/").filter(Boolean);
  return [
    { label: "全部", prefix: "" },
    ...parts.map((part, index) => ({
      label: part,
      prefix: `${parts.slice(0, index + 1).join("/")}/`,
    })),
  ];
};

const getFolderName = (prefix) => {
  const parts = String(prefix || "").split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

const getFolderParentPrefix = (prefix) => {
  const parts = String(prefix || "").split("/").filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join("/")}/` : "";
};

const getKeyParentPrefix = (key) => {
  const parts = String(key || "").split("/").filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join("/")}/` : "";
};

const resolveFolderPrefix = (value, currentPrefix = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = normalizePrefix(raw);
  const shouldNest = currentPrefix && !raw.includes("/");
  return normalizePrefix(`${shouldNest ? currentPrefix : ""}${normalized}`);
};

const assetDialog = Swal.mixin({
  confirmButtonColor: "#2563eb",
  cancelButtonColor: "#64748b",
  reverseButtons: true,
  focusCancel: true,
});

const assetDialogHtml = ({ subject = "", message, note = "" }) => `
  <style>
    .asset-dialog-subject { border: 1px solid #dbe3ee; background: #f8fafc; }
    .asset-dialog-note { color: #64748b; }
    [data-auth-theme="night"] .asset-dialog-subject {
      border-color: var(--app-border, #555);
      background: var(--app-surface-soft, #333);
      color: var(--app-text, #eee);
    }
    [data-auth-theme="night"] .asset-dialog-note { color: var(--app-muted-text, #bebebe); }
  </style>
  <div style="text-align:left;line-height:1.65">
    ${subject ? `<div class="asset-dialog-subject" style="padding:10px 12px;border-radius:8px;font-family:monospace;font-size:13px;font-weight:700;overflow-wrap:anywhere">${escapeHtml(subject)}</div>` : ""}
    <div style="margin-top:${subject ? "12px" : "0"}">${escapeHtml(message)}</div>
    ${note ? `<div class="asset-dialog-note" style="margin-top:12px;font-size:13px">${escapeHtml(note)}</div>` : ""}
  </div>
`;

const showAssetError = ({ title, message, subject = "", note = "請稍後再試。" }) =>
  assetDialog.fire({
    icon: "error",
    title,
    html: assetDialogHtml({ subject, message, note }),
    confirmButtonText: "知道了",
  });

const swalToast = (icon, title) =>
  assetDialog.fire({
    icon,
    title,
    toast: true,
    position: "top-end",
    timer: 1800,
    timerProgressBar: true,
    showConfirmButton: false,
  });

const confirmDanger = ({ title, text, confirmButtonText = "刪除" }) =>
  assetDialog.fire({
    icon: "warning",
    title,
    text,
    input: "text",
    inputLabel: "請輸入 delete 確認刪除",
    inputPlaceholder: "delete",
    inputAttributes: {
      autocapitalize: "off",
      autocomplete: "off",
      spellcheck: "false",
    },
    inputValidator: (value) =>
      String(value || "").trim() === "delete" ? null : "請輸入 delete 才能刪除。",
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "取消",
    confirmButtonColor: "#b3261e",
  });

export default function CloudflareFileManager() {
  const managerRef = useRef(null);
  const fileInputRef = useRef(null);
  const filesRef = useRef([]);
  const uploadAbortRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [remoteFiles, setRemoteFiles] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [currentPrefix, setCurrentPrefix] = useState("");
  const [uploadPrefix, setUploadPrefix] = useState("images/");
  const [newFolderPath, setNewFolderPath] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPrefixes, setExpandedPrefixes] = useState(() => new Set([""]));
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingKeys, setDownloadingKeys] = useState([]);
  const [downloadingFolderPrefix, setDownloadingFolderPrefix] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [deletingFolderPrefix, setDeletingFolderPrefix] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deletingKeys, setDeletingKeys] = useState([]);
  const [movingKeys, setMovingKeys] = useState([]);
  const [dragMoveKeys, setDragMoveKeys] = useState([]);
  const [dragTargetPrefix, setDragTargetPrefix] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [message, setMessage] = useState("");
  const [copyMessage, setCopyMessage] = useState("");

  const folderTree = useMemo(() => buildTree(remoteFiles), [remoteFiles]);
  const expandablePrefixes = useMemo(() => getExpandablePrefixes(folderTree), [folderTree]);
  const allFoldersExpanded =
    expandablePrefixes.length > 0 &&
    expandablePrefixes.every((prefix) => expandedPrefixes.has(prefix));
  const treeNodes = useMemo(
    () => flattenTree(folderTree, expandedPrefixes),
    [expandedPrefixes, folderTree],
  );
  const visibleRemoteFiles = useMemo(
    () => remoteFiles.filter((file) => !isFolderMarkerKey(file.key)),
    [remoteFiles],
  );
  const currentRemoteFiles = useMemo(
    () => visibleRemoteFiles.filter((file) => !currentPrefix || file.key.startsWith(currentPrefix)),
    [currentPrefix, visibleRemoteFiles],
  );
  const currentStorageSize = useMemo(
    () =>
      currentRemoteFiles.reduce((sum, file) => sum + Number(file.size || 0), 0),
    [currentRemoteFiles],
  );
  const currentFolders = useMemo(
    () => getImmediateFolders(remoteFiles, currentPrefix),
    [currentPrefix, remoteFiles],
  );
  const breadcrumbs = useMemo(() => getBreadcrumbs(currentPrefix), [currentPrefix]);
  const filteredRemoteFiles = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return currentRemoteFiles
      .filter((file) => !keyword || file.key.toLowerCase().includes(keyword))
      .sort((a, b) => a.key.localeCompare(b.key, "zh-Hant"));
  }, [currentRemoteFiles, searchTerm]);
  const hasSearch = Boolean(searchTerm.trim());

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const deletingKeySet = useMemo(() => new Set(deletingKeys), [deletingKeys]);
  const downloadingKeySet = useMemo(() => new Set(downloadingKeys), [downloadingKeys]);
  const movingKeySet = useMemo(() => new Set(movingKeys), [movingKeys]);
  const initialLoading = loadingFiles && remoteFiles.length === 0;
  const allVisibleSelected =
    filteredRemoteFiles.length > 0 &&
    filteredRemoteFiles.every((file) => selectedSet.has(file.key));
  const uploadStats = useMemo(
    () =>
      files.reduce(
        (stats, item) => {
          stats.total += 1;
          if (item.status === "done") stats.done += 1;
          if (item.status === "error") stats.error += 1;
          if (item.status === "cancelled") stats.cancelled += 1;
          if (item.status === "uploading") stats.uploading += 1;
          return stats;
        },
        { total: 0, done: 0, error: 0, cancelled: 0, uploading: 0 },
      ),
    [files],
  );

  const showMessage = (text, isCopy = false) => {
    if (isCopy) {
      setCopyMessage(text);
      window.setTimeout(() => setCopyMessage(""), 1600);
      return;
    }
    setMessage(text);
  };

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    return () => {
      uploadAbortRef.current?.abort();
      filesRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  const copyText = async (text, successText = "已複製") => {
    try {
      await navigator.clipboard.writeText(text);
      showMessage(successText, true);
    } catch {
      showMessage("複製失敗");
    }
  };

  const fetchRemoteFiles = useCallback(async () => {
    setLoadingFiles(true);
    setMessage("");

    const params = new URLSearchParams();
    params.set("all", "true");

    try {
      const response = await fetch(`${apiUrl}/assets/list/?${params}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "讀取檔案清單失敗");
      }
      const nextFiles = result.files || result.objects || [];
      setRemoteFiles(nextFiles);
      setSelectedKeys((prev) =>
        prev.filter((key) =>
          nextFiles.some((file) => file.key === key && !isFolderMarkerKey(file.key)),
        ),
      );
    } catch (error) {
      setRemoteFiles([]);
      showMessage(error.message || "讀取檔案清單失敗");
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    fetchRemoteFiles();
  }, [fetchRemoteFiles]);

  useEffect(() => {
    setSearchTerm("");
    if (!currentPrefix) return;
    setExpandedPrefixes((current) => {
      const next = new Set(current);
      getPrefixAncestors(currentPrefix).forEach((prefix) => next.add(prefix));
      return next;
    });
  }, [currentPrefix]);

  const toggleTreeFolder = useCallback((prefix) => {
    if (!prefix) return;
    setExpandedPrefixes((current) => {
      const next = new Set(current);
      if (next.has(prefix)) {
        next.delete(prefix);
      } else {
        next.add(prefix);
      }
      return next;
    });
  }, []);

  const toggleAllTreeFolders = useCallback(() => {
    setExpandedPrefixes((current) => {
      if (expandablePrefixes.length === 0) return current;
      const shouldCollapseAll = expandablePrefixes.every((prefix) => current.has(prefix));
      return shouldCollapseAll ? new Set([""]) : new Set(["", ...expandablePrefixes]);
    });
  }, [expandablePrefixes]);

  const handleFiles = (selected) => {
    const nextFiles = Array.from(selected || []).map((item) => {
      const file = item?.file || item;
      const relativePath = normalizeKey(
        item?.relativePath || file.webkitRelativePath || file.name,
      );
      return {
        file,
        relativePath,
        status: "idle",
        url: "",
        key: "",
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      };
    });
    setFiles((prev) => [...prev, ...nextFiles]);
  };

  useEffect(() => {
    const handlePaste = (event) => {
      if (!isVisiblePasteRoot(managerRef.current)) return;

      const pastedFiles = getClipboardFiles(event.clipboardData);
      if (!pastedFiles.length) return;

      event.preventDefault();
      setDragOver(false);
      handleFiles(pastedFiles);
      showMessage(`已從剪貼簿加入 ${pastedFiles.length} 個檔案。`);
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  });

  const clearQueuedFiles = () => {
    files.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    setFiles([]);
  };

  const updateFile = (index, patch) => {
    setFiles((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const uploadFile = async (item, index, signal) => {
    const targetKey = normalizeKey(
      `${normalizePrefix(uploadPrefix)}${item.relativePath || item.file.name}`,
    );
    const formData = new FormData();
    formData.append("file", item.file);
    formData.append("key", targetKey);
    updateFile(index, { status: "uploading" });

    try {
      const response = await fetch(`${apiUrl}/assets/upload/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
        signal,
      });
      const result = await response.json();
      if (!response.ok || !result.success || !result.key) {
        throw new Error(result.error || "上傳失敗");
      }
      const url = getPublicUrl(result.key);
      updateFile(index, { status: "done", key: result.key, url });
      return url;
    } catch (error) {
      if (error.name === "AbortError") {
        updateFile(index, { status: "cancelled" });
        return "";
      }
      updateFile(index, { status: "error" });
      showMessage(error.message || "上傳失敗");
      return "";
    }
  };

  const uploadAll = async () => {
    if (!files.length) return;
    const abortController = new AbortController();
    let failedCount = 0;
    uploadAbortRef.current = abortController;
    setUploading(true);
    setMessage("");
    for (let index = 0; index < files.length; index += 1) {
      if (abortController.signal.aborted) break;
      if (files[index].status === "done") continue;
      const uploadedUrl = await uploadFile(files[index], index, abortController.signal);
      if (!uploadedUrl && !abortController.signal.aborted) failedCount += 1;
    }
    if (abortController.signal.aborted) {
      setFiles((prev) =>
        prev.map((item) =>
          item.status === "idle" || item.status === "uploading"
            ? { ...item, status: "cancelled" }
            : item,
        ),
      );
      showMessage("上傳已終止。");
    }
    if (uploadAbortRef.current === abortController) {
      uploadAbortRef.current = null;
    }
    setUploading(false);
    await fetchRemoteFiles();
    if (failedCount > 0) {
      await showAssetError({
        title: "部分檔案上傳失敗",
        message: `${failedCount} 個檔案沒有完成上傳，請查看待上傳清單中的失敗項目。`,
        note: "已成功上傳的檔案會保留；可以直接重新執行尚未完成的項目。",
      });
    }
  };

  const cancelUpload = () => {
    uploadAbortRef.current?.abort();
  };

  const createFolder = async () => {
    const prefix = resolveFolderPrefix(newFolderPath || uploadPrefix, currentPrefix).replace(/\/$/, "");
    if (!prefix) {
      showMessage("請輸入資料夾位置。");
      return;
    }
    setCreatingFolder(true);
    try {
      const response = await fetch(`${apiUrl}/assets/folder/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ prefix }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "建立資料夾失敗");
      }
      setNewFolderPath("");
      setUploadPrefix(`${prefix}/`);
      setCurrentPrefix(`${prefix}/`);
      await fetchRemoteFiles();
      showMessage("資料夾已建立。");
      swalToast("success", "資料夾已建立");
    } catch (error) {
      showMessage(error.message || "建立資料夾失敗");
      showAssetError({
        title: "建立資料夾失敗",
        subject: prefix,
        message: error.message || "無法建立資料夾。",
        note: "尚未建立任何資料夾，既有檔案不受影響。",
      });
    } finally {
      setCreatingFolder(false);
    }
  };

  const renameFolder = async (sourcePrefixValue, targetPrefixValue) => {
    const sourcePrefix = normalizePrefix(sourcePrefixValue);
    const targetPrefix = resolveFolderPrefix(targetPrefixValue, getFolderParentPrefix(sourcePrefix));
    if (!sourcePrefix || !targetPrefix || sourcePrefix === targetPrefix) {
      return;
    }
    setRenaming(true);
    try {
      const result = await confirmAssetRelocation({
        source_prefix: sourcePrefix,
        target_prefix: targetPrefix,
      });
      if (!result) return;
      const updatePrefix = (prefix) => prefix.startsWith(sourcePrefix)
        ? targetPrefix + prefix.slice(sourcePrefix.length) : prefix;
      setCurrentPrefix(updatePrefix);
      setUploadPrefix(updatePrefix);
      await fetchRemoteFiles();
      showMessage(`資料夾已更新，已連動 ${result.question_count} 題與 ${result.draft_count} 份草稿。`);
      if (!result.cleanup_pending?.length) swalToast("success", "資料夾與題庫引用已更新");
    } catch (error) {
      showMessage(error.message || "資料夾改名失敗");
      showAssetError({
        title: "資料夾改名失敗",
        subject: sourcePrefix,
        message: error.message || "無法更新資料夾路徑。",
        note: "請確認新路徑；題庫引用未完成更新時，原始檔案會保留。",
      });
    } finally {
      setRenaming(false);
    }
  };

  const openRenameFolderDialog = async (prefix) => {
    const sourcePrefix = normalizePrefix(prefix);
    const result = await assetDialog.fire({
      icon: "question",
      title: "編輯資料夾路徑",
      input: "text",
      inputValue: sourcePrefix,
      inputLabel: "新的資料夾路徑",
      inputPlaceholder: "例如 images/2026/course-a/",
      showCancelButton: true,
      confirmButtonText: "儲存",
      cancelButtonText: "取消",
      inputValidator: (value) => {
        const targetPrefix = resolveFolderPrefix(value, getFolderParentPrefix(sourcePrefix));
        if (!targetPrefix) return "請輸入資料夾路徑。";
        if (targetPrefix === sourcePrefix) return "路徑沒有變更。";
        return null;
      },
    });
    if (!result.isConfirmed) return;
    await renameFolder(sourcePrefix, result.value);
  };

  const deleteFolder = async (prefix) => {
    const targetPrefix = normalizePrefix(prefix);
    if (!targetPrefix) {
      return;
    }
    const result = await confirmDanger({
      title: "刪除整個資料夾？",
      text: `${targetPrefix} 底下所有檔案都會一起刪除。`,
    });
    if (!result.isConfirmed) return;
    setDeletingFolderPrefix(targetPrefix);
    try {
      const response = await fetch(`${apiUrl}/assets/delete/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ prefix: targetPrefix }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || "資料夾刪除失敗");
      }
      setCurrentPrefix((prev) =>
        prev === targetPrefix || prev.startsWith(targetPrefix)
          ? getFolderParentPrefix(targetPrefix)
          : prev,
      );
      setUploadPrefix((prev) =>
        prev === targetPrefix || prev.startsWith(targetPrefix)
          ? getFolderParentPrefix(targetPrefix)
          : prev,
      );
      await fetchRemoteFiles();
      showMessage(`資料夾已刪除，移除 ${result.deleted_count || 0} 個檔案。`);
      swalToast("success", "資料夾已刪除");
    } catch (error) {
      showMessage(error.message || "資料夾刪除失敗");
      showAssetError({
        title: "資料夾刪除失敗",
        subject: targetPrefix,
        message: error.message || "無法刪除資料夾。",
        note: "未成功刪除的檔案仍保留在 Cloudflare。",
      });
    } finally {
      setDeletingFolderPrefix("");
    }
  };

  const downloadFile = async (key, { track = true } = {}) => {
    if (track) setDownloadingKeys((prev) => Array.from(new Set([...prev, key])));
    try {
      const response = await fetch(
        `${apiUrl}/assets/download/?key=${encodeURIComponent(key)}`,
        { headers: getAuthHeaders() },
      );
      if (!response.ok) throw new Error(`下載失敗：${key}`);
      downloadBlob(await response.blob(), getFilename(key));
    } catch (error) {
      if (track) {
        const message = error.message || "無法下載檔案。";
        showMessage(message);
        await showAssetError({
          title: "檔案下載失敗",
          subject: key,
          message,
          note: "Cloudflare 上的原始檔案不會改變。",
        });
      }
      throw error;
    } finally {
      if (track) {
        setDownloadingKeys((prev) => prev.filter((item) => item !== key));
      }
    }
  };

  const downloadFolder = async (prefix, { all = false } = {}) => {
    const targetPrefix = normalizePrefix(prefix);
    if (!targetPrefix && !all) return;
    const downloadId = all ? "__all__" : targetPrefix;
    const downloadLabel = all ? "Cloudflare 全部檔案" : targetPrefix;
    const sourceFiles = visibleRemoteFiles.filter(
      (file) => (all || file.key.startsWith(targetPrefix)) && !isFolderMarkerKey(file.key),
    );
    const sourceBytes = sourceFiles
      .reduce((sum, file) => sum + Number(file.size || 0), 0);
    const isLargeExport = sourceBytes >= 500 * 1024 * 1024 || sourceFiles.length >= 300;
    if (isLargeExport) {
      const confirmation = await assetDialog.fire({
        icon: "warning",
        title: "大型 ZIP 臨時匯出",
        html: assetDialogHtml({
          subject: `${downloadLabel} · ${sourceFiles.length} 個檔案 · ${formatBytes(sourceBytes)}`,
          message: "瀏覽器會直接串流下載，但如果網路中斷，ZIP 必須從頭重新開始。",
          note: "完整或定期備份建議使用 Mac 的「Cloudflare 素材備份.app」，可增量下載並接續未完成檔案。",
        }),
        showCancelButton: true,
        confirmButtonText: "仍要下載 ZIP",
        cancelButtonText: "取消",
      });
      if (!confirmation.isConfirmed) return;
    }
    let shouldRetry = false;
    setDownloadingFolderPrefix(downloadId);
    setMessage("");
    try {
      const response = await fetch(
        `${apiUrl}/assets/download-folder/ticket/`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ prefix: targetPrefix, all }),
        },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        const error = new Error(result?.error || "資料夾下載失敗");
        error.status = response.status;
        throw error;
      }
      if (!result?.download_url) {
        throw new Error("伺服器沒有建立下載連結。");
      }
      const nativeDownloadUrl = new URL(result.download_url, response.url).toString();
      const anchor = document.createElement("a");
      anchor.href = nativeDownloadUrl;
      anchor.download = result.filename || "cloudflare-assets.zip";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      showMessage(
        `${downloadLabel} ZIP 已開始下載（${formatBytes(result.source_bytes ?? sourceBytes)}）。`,
      );
    } catch (error) {
      const reason = error.status === 413
        ? "檔案數量或總容量超過網站單次 ZIP 限制。請改用 Mac 備份 App，或分別匯出較小的資料夾。"
        : [401, 403].includes(error.status)
          ? "登入狀態或權限已失效，請重新登入後再試。"
          : [502, 503, 504].includes(error.status)
            ? "Cloudflare 暫時沒有完成檔案讀取，請稍後重試。"
            : error.message || "網路連線中斷或伺服器沒有完成 ZIP 打包。";
      showMessage(`${downloadLabel}下載失敗：${reason}`);
      const result = await assetDialog.fire({
        icon: "error",
        title: all ? "全部下載失敗" : "資料夾下載失敗",
        html: assetDialogHtml({
          subject: downloadLabel,
          message: reason,
          note: error.status === 413
            ? "Mac 的「Cloudflare 素材備份.app」會逐檔增量下載，不受大型 ZIP 記憶體限制。"
            : "下載失敗不會修改或刪除 Cloudflare 裡的任何檔案。",
        }),
        showCancelButton: true,
        confirmButtonText: "重試",
        cancelButtonText: "關閉",
      });
      shouldRetry = result.isConfirmed;
    } finally {
      setDownloadingFolderPrefix("");
    }
    if (shouldRetry) {
      await downloadFolder(prefix, { all });
    }
  };

  const downloadSelected = async () => {
    if (!selectedKeys.length) return;
    setDownloading(true);
    setMessage("");
    try {
      for (const key of selectedKeys) {
        await downloadFile(key, { track: false });
      }
    } catch (error) {
      const message = error.message || "選取檔案下載失敗";
      showMessage(message);
      await showAssetError({
        title: "選取檔案下載失敗",
        message,
        note: "已下載的檔案會保留；Cloudflare 上的原始檔案不會改變。",
      });
    } finally {
      setDownloading(false);
    }
  };

  const deleteKey = async (key) => {
    setDeletingKeys((prev) => Array.from(new Set([...prev, key])));
    try {
      const response = await fetch(`${apiUrl}/assets/delete/`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ key }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || `刪除失敗：${key}`);
      }
    } finally {
      setDeletingKeys((prev) => prev.filter((item) => item !== key));
    }
  };

  const deleteSelected = async () => {
    if (!selectedKeys.length) {
      return;
    }
    const result = await confirmDanger({
      title: `刪除 ${selectedKeys.length} 個檔案？`,
      text: "選取的檔案會從 Cloudflare 移除。",
      confirmButtonText: "刪除選取",
    });
    if (!result.isConfirmed) return;
    try {
      for (const key of selectedKeys) {
        await deleteKey(key);
      }
      setSelectedKeys([]);
      await fetchRemoteFiles();
      showMessage("已刪除選取檔案。");
      swalToast("success", "已刪除選取檔案");
    } catch (error) {
      showMessage(error.message || "刪除失敗");
      showAssetError({
        title: "刪除失敗",
        message: error.message || "無法刪除選取的檔案。",
        note: "未成功刪除的檔案仍保留在 Cloudflare。",
      });
    }
  };

  const renameKey = async (sourceKeyValue, targetKeyValue) => {
    const sourceKey = normalizeKey(sourceKeyValue);
    const targetKey = normalizeKey(targetKeyValue);
    if (!sourceKey || !targetKey || sourceKey === targetKey) {
      return;
    }
    setRenaming(true);
    try {
      const result = await confirmAssetRelocation({ source_key: sourceKey, target_key: targetKey });
      if (!result) return;
      await fetchRemoteFiles();
      showMessage(`路徑已更新，已連動 ${result.question_count} 題與 ${result.draft_count} 份草稿。`);
      if (!result.cleanup_pending?.length) swalToast("success", "路徑與題庫引用已更新");
    } catch (error) {
      showMessage(error.message || "改名失敗");
      showAssetError({
        title: "改名失敗",
        subject: sourceKey,
        message: error.message || "無法更新檔案路徑。",
        note: "題庫引用未完成更新時，原始檔案會保留。",
      });
    } finally {
      setRenaming(false);
    }
  };

  const openRenameFileDialog = async (key) => {
    const sourceKey = normalizeKey(key);
    const result = await assetDialog.fire({
      icon: "question",
      title: "編輯檔案路徑",
      input: "text",
      inputValue: sourceKey,
      inputLabel: "新的檔案路徑",
      inputPlaceholder: "例如 images/2026/photo.png",
      showCancelButton: true,
      confirmButtonText: "儲存",
      cancelButtonText: "取消",
      inputValidator: (value) => {
        const targetKey = normalizeKey(value);
        if (!targetKey) return "請輸入檔案路徑。";
        if (targetKey === sourceKey) return "路徑沒有變更。";
        return null;
      },
    });
    if (!result.isConfirmed) return;
    await renameKey(sourceKey, result.value);
  };

  const getMoveKeys = (sourceKey) => {
    if (selectedSet.has(sourceKey)) {
      return selectedKeys.filter((key) =>
        remoteFiles.some((file) => file.key === key && !isFolderMarkerKey(file.key)),
      );
    }
    return [sourceKey];
  };

  const moveKeysToPrefix = async (keys, targetPrefixValue) => {
    const targetPrefix = normalizePrefix(targetPrefixValue);
    const uniqueKeys = Array.from(new Set(keys.map(normalizeKey).filter(Boolean)));
    if (!uniqueKeys.length) return;

    const moves = uniqueKeys
      .map((sourceKey) => ({
        sourceKey,
        targetKey: normalizeKey(`${targetPrefix}${getFilename(sourceKey)}`),
      }))
      .filter(({ sourceKey, targetKey }) => sourceKey && targetKey && sourceKey !== targetKey);

    if (!moves.length) {
      showMessage("檔案已經在這個資料夾。");
      return;
    }

    const movingKeySet = new Set(moves.map((move) => move.sourceKey));

    setMovingKeys(moves.map((move) => move.sourceKey));
    setMessage("");
    try {
      const result = await confirmAssetRelocation({
        moves: moves.map(({ sourceKey, targetKey }) => ({ source_key: sourceKey, target_key: targetKey })),
      });
      if (!result) return;
      setSelectedKeys((prev) =>
        prev
          .filter((key) => !movingKeySet.has(key))
          .concat(moves.map((move) => move.targetKey)),
      );
      await fetchRemoteFiles();
      showMessage(`已移動 ${moves.length} 個檔案，已連動 ${result.question_count} 題與 ${result.draft_count} 份草稿。`);
      if (!result.cleanup_pending?.length) swalToast("success", "檔案與題庫引用已更新");
    } catch (error) {
      showMessage(error.message || "移動失敗");
      showAssetError({
        title: "移動失敗",
        subject: targetPrefix || "根目錄",
        message: error.message || "無法移動檔案。",
        note: "題庫引用未完成更新時，原始檔案會保留。",
      });
    } finally {
      setMovingKeys([]);
    }
  };

  const openMoveSelectedDialog = async () => {
    if (!selectedKeys.length) return;
    const result = await assetDialog.fire({
      icon: "question",
      title: "移動選取檔案",
      input: "text",
      inputValue: currentPrefix,
      inputLabel: "目標資料夾路徑",
      inputPlaceholder: "例如 好題蒐集/題型圖示/",
      showCancelButton: true,
      confirmButtonText: "移動",
      cancelButtonText: "取消",
      inputValidator: (value) => {
        const targetPrefix = normalizePrefix(value);
        if (selectedKeys.every((key) => getKeyParentPrefix(key) === targetPrefix)) {
          return "選取檔案已經都在這個資料夾。";
        }
        return null;
      },
    });
    if (!result.isConfirmed) return;
    await moveKeysToPrefix(selectedKeys, result.value);
  };

  const handleRemoteDragStart = (event, sourceKey) => {
    const keys = getMoveKeys(sourceKey);
    setDragMoveKeys(keys);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-sunny-asset-keys", JSON.stringify(keys));
    event.dataTransfer.setData("text/plain", keys.join("\n"));
  };

  const handleRemoteDragEnd = () => {
    setDragMoveKeys([]);
    setDragTargetPrefix("");
  };

  const readDraggedAssetKeys = (event) => {
    try {
      const data = event.dataTransfer.getData("application/x-sunny-asset-keys");
      return data ? JSON.parse(data) : dragMoveKeys;
    } catch {
      return dragMoveKeys;
    }
  };

  const handleMoveDragOver = (event, prefix) => {
    if (!dragMoveKeys.length) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDragTargetPrefix(normalizePrefix(prefix));
  };

  const handleMoveDragLeave = (prefix) => {
    const normalized = normalizePrefix(prefix);
    setDragTargetPrefix((prev) => (prev === normalized ? "" : prev));
  };

  const handleMoveDrop = async (event, prefix) => {
    const keys = readDraggedAssetKeys(event);
    if (!keys.length) return;
    event.preventDefault();
    event.stopPropagation();
    setDragMoveKeys([]);
    setDragTargetPrefix("");
    await moveKeysToPrefix(keys, prefix);
  };

  const openFilePreview = async (file) => {
    const publicUrl = getPublicUrl(file.key);
    const filename = getFilename(file.key);
    const previewType = getPreviewType(file.key);
    const previewUrl = previewType === "word" ? getOfficeViewerUrl(publicUrl) : publicUrl;
    const safeUrl = escapeHtml(previewUrl);
    const safeFilename = escapeHtml(filename);
    const safeKey = escapeHtml(file.key);
    const safeSize = escapeHtml(formatBytes(file.size));
    const safeUploaded = escapeHtml(formatDate(file.uploaded));
    const previewHtml =
      previewType === "image"
        ? `<img class="asset-preview-image" src="${safeUrl}" alt="${safeFilename}" />`
        : previewType === "pdf" || previewType === "word"
          ? `<div class="asset-preview-loader" id="asset-preview-loader">${previewType === "word" ? "Word 預覽載入中..." : "PDF 載入中..."}</div><iframe class="asset-preview-frame" src="${safeUrl}" title="${safeFilename}" onload="document.getElementById('asset-preview-loader')?.classList.add('is-hidden')" onerror="document.getElementById('asset-preview-loader') && (document.getElementById('asset-preview-loader').textContent = '預覽無法在視窗內顯示，請開新分頁。')"></iframe>`
          : previewType === "video"
            ? `<video class="asset-preview-media" src="${safeUrl}" controls playsinline></video>`
            : previewType === "audio"
              ? `<audio class="asset-preview-audio" src="${safeUrl}" controls></audio>`
              : `<div class="asset-preview-empty"><strong>${safeFilename}</strong><span>此檔案類型無法直接預覽。</span></div>`;

    const result = await assetDialog.fire({
      title: filename,
      html: `
        <style>
          .asset-preview-shell {
            display: grid;
            gap: 12px;
            text-align: left;
          }
          .asset-preview-canvas {
            position: relative;
            display: grid;
            place-items: center;
            min-height: 220px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: #f8fafc;
            overflow: hidden;
          }
          .asset-preview-image {
            display: block;
            max-width: 100%;
            max-height: 68vh;
            object-fit: contain;
            background: #fff;
          }
          .asset-preview-frame {
            width: min(100%, 860px);
            height: 68vh;
            border: 0;
            background: #fff;
          }
          .asset-preview-loader {
            position: absolute;
            inset: 0;
            z-index: 1;
            display: grid;
            place-items: center;
            background: #f8fafc;
            color: #4b5563;
            font-size: 14px;
            font-weight: 750;
          }
          .asset-preview-loader::before {
            content: "";
            width: 22px;
            height: 22px;
            margin-right: 9px;
            border: 3px solid #d1d5db;
            border-top-color: #111827;
            border-radius: 999px;
            animation: asset-preview-spin 0.8s linear infinite;
          }
          .asset-preview-loader.is-hidden {
            display: none;
          }
          @keyframes asset-preview-spin {
            to {
              transform: rotate(360deg);
            }
          }
          .asset-preview-media {
            width: min(100%, 860px);
            max-height: 68vh;
            background: #111827;
          }
          .asset-preview-audio {
            width: min(100%, 640px);
          }
          .asset-preview-empty {
            display: grid;
            gap: 8px;
            justify-items: center;
            padding: 34px 18px;
            color: #4b5563;
          }
          .asset-preview-empty strong {
            color: #111827;
            overflow-wrap: anywhere;
          }
          .asset-preview-meta {
            display: grid;
            gap: 4px;
            color: #4b5563;
            font-size: 12px;
            overflow-wrap: anywhere;
          }
          [data-auth-theme="night"] .asset-preview-loader {
            background: var(--app-surface-soft, #333);
            color: var(--app-muted-text, #bebebe);
          }
          [data-auth-theme="night"] .asset-preview-loader::before {
            border-color: var(--app-border, #555);
            border-top-color: var(--app-text, #eee);
          }
          [data-auth-theme="night"] .asset-preview-empty,
          [data-auth-theme="night"] .asset-preview-meta {
            color: var(--app-muted-text, #bebebe);
          }
          [data-auth-theme="night"] .asset-preview-empty {
            width: 100%;
            background: var(--app-surface-soft, #333);
          }
          [data-auth-theme="night"] .asset-preview-empty strong {
            color: var(--app-text, #eee);
          }
        </style>
        <div class="asset-preview-shell">
          <div class="asset-preview-canvas">${previewHtml}</div>
          <div class="asset-preview-meta">
            <span>${safeKey}</span>
            <span>${safeSize} · ${safeUploaded}</span>
          </div>
        </div>
      `,
      width: previewType === "audio" || previewType === "file" ? 720 : 980,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: previewType === "file" ? "下載" : "開新分頁",
      denyButtonText: "複製 URL",
      cancelButtonText: "關閉",
      denyButtonColor: "#4b5563",
    });

    if (result.isConfirmed) {
      if (previewType === "file") {
        await downloadFile(file.key);
      } else {
        window.open(previewUrl, "_blank", "noopener,noreferrer");
      }
    }
    if (result.isDenied) {
      await copyText(publicUrl, "URL 已複製");
    }
  };

  const toggleVisibleKeys = () => {
    const visibleKeys = filteredRemoteFiles.map((file) => file.key);
    setSelectedKeys((prev) => {
      if (allVisibleSelected) {
        return prev.filter((key) => !visibleKeys.includes(key));
      }
      return Array.from(new Set([...prev, ...visibleKeys]));
    });
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  return (
    <Container ref={managerRef}>
      <Header>
        <TitleGroup>
          <Eyebrow>Asset Manager</Eyebrow>
          <Title>Cloudflare 檔案管理</Title>
        </TitleGroup>
        <SecondaryButton type="button" onClick={fetchRemoteFiles} disabled={loadingFiles}>
          {loadingFiles ? <SpinningIcon /> : <FaSyncAlt />}
          {loadingFiles ? "讀取中" : "重新整理"}
        </SecondaryButton>
      </Header>

      <StatsGrid>
        <Stat>
          <span>總檔案</span>
          <strong>{visibleRemoteFiles.length}</strong>
        </Stat>
        <Stat>
          <span>目前位置</span>
          <strong>{filteredRemoteFiles.length}</strong>
        </Stat>
        <Stat>
          <span>已選取</span>
          <strong>{selectedKeys.length}</strong>
        </Stat>
        <Stat>
          <span>容量</span>
          <strong>{formatBytes(currentStorageSize)}</strong>
        </Stat>
      </StatsGrid>

      <ManagerGrid>
        <SidePanel>
          <PanelToggleTitle
            type="button"
            aria-expanded={allFoldersExpanded}
            onClick={toggleAllTreeFolders}
          >
            <span>
              <FaFolder />
              資料結構
            </span>
            {allFoldersExpanded ? <FaChevronDown /> : <FaChevronRight />}
          </PanelToggleTitle>
          <TreeList
            $dropTarget={dragTargetPrefix === normalizePrefix(currentPrefix)}
            onDragOver={(event) => handleMoveDragOver(event, currentPrefix)}
            onDragLeave={() => handleMoveDragLeave(currentPrefix)}
            onDrop={(event) => handleMoveDrop(event, currentPrefix)}
          >
            {initialLoading ? (
              <SkeletonGroup>
                {Array.from({ length: 7 }).map((_, index) => (
                  <SkeletonLine key={index} $width={`${92 - index * 5}%`} />
                ))}
              </SkeletonGroup>
            ) : (
              treeNodes.map((node) => (
                <TreeItem
                  key={node.prefix || "root"}
                  $active={currentPrefix === node.prefix}
                  $dropTarget={dragTargetPrefix === normalizePrefix(node.prefix)}
                  style={{ paddingLeft: 12 + node.level * 14 }}
                  onDragOver={(event) => handleMoveDragOver(event, node.prefix)}
                  onDragLeave={() => handleMoveDragLeave(node.prefix)}
                  onDrop={(event) => handleMoveDrop(event, node.prefix)}
                >
                  <TreeToggleButton
                    type="button"
                    aria-label={node.expanded ? "折疊資料夾" : "展開資料夾"}
                    aria-expanded={node.hasChildren && node.prefix ? node.expanded : undefined}
                    disabled={!node.hasChildren || !node.prefix}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!node.hasChildren || !node.prefix) return;
                      toggleTreeFolder(node.prefix);
                    }}
                  >
                    {node.hasChildren && node.prefix ? (
                      node.expanded ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )
                    ) : null}
                  </TreeToggleButton>
                  <TreeButton
                    type="button"
                    $active={currentPrefix === node.prefix}
                    aria-expanded={node.hasChildren && node.prefix ? node.expanded : undefined}
                    onClick={() => {
                      if (node.hasChildren && node.prefix) {
                        toggleTreeFolder(node.prefix);
                      }
                      setCurrentPrefix(node.prefix);
                      if (node.prefix) setUploadPrefix(node.prefix);
                    }}
                  >
                    <span>{node.name}</span>
                    <small>{node.count}</small>
                  </TreeButton>
                  <TreeCopyButton
                    type="button"
                    $active={currentPrefix === node.prefix}
                    title="複製資料夾路徑"
                    onClick={(event) => {
                      event.stopPropagation();
                      copyText(node.prefix, "資料夾路徑已複製");
                    }}
                  >
                    <FaCopy />
                  </TreeCopyButton>
                </TreeItem>
              ))
            )}
          </TreeList>

          <PanelTitle>
            <FaPlus />
            建立資料夾
          </PanelTitle>
          <FolderHint>
            <span>目前位置</span>
            <strong>{currentPrefix || "根目錄"}</strong>
          </FolderHint>
          <InlineStack>
            <TextInput
              value={newFolderPath}
              onChange={(event) => setNewFolderPath(event.target.value)}
              placeholder={currentPrefix ? `例如 ${getFolderName(currentPrefix)} 的子資料夾` : "例如 images/2026/course-a"}
            />
            <HelperText>
              輸入單一名稱會建立在目前位置；輸入含 / 的路徑可一次建立多層。
            </HelperText>
            <SecondaryButton type="button" onClick={createFolder} disabled={creatingFolder}>
              {creatingFolder ? <SpinningIcon /> : <FaPlus />}
              {creatingFolder ? "建立中" : "建立"}
            </SecondaryButton>
          </InlineStack>
        </SidePanel>

        <MainPanel>
          <Section>
            <SectionHeader>
              <SectionTitle>
                <FaCloudUploadAlt />
                上傳圖片 / 文件
              </SectionTitle>
              <Breadcrumbs>
                {breadcrumbs.map((item, index) => (
                  <BreadcrumbButton
                    key={item.prefix || "root"}
                    type="button"
                    $dropTarget={dragTargetPrefix === normalizePrefix(item.prefix)}
                    onDragOver={(event) => handleMoveDragOver(event, item.prefix)}
                    onDragLeave={() => handleMoveDragLeave(item.prefix)}
                    onDrop={(event) => handleMoveDrop(event, item.prefix)}
                    onClick={() => {
                      setCurrentPrefix(item.prefix);
                      if (item.prefix) setUploadPrefix(item.prefix);
                    }}
                  >
                    {index > 0 && <span>/</span>}
                    {item.label}
                  </BreadcrumbButton>
                ))}
              </Breadcrumbs>
            </SectionHeader>

            <FieldGrid>
              <Field>
                <FieldLabel>指定上傳位置</FieldLabel>
                <TextInput
                  value={uploadPrefix}
                  onChange={(event) => setUploadPrefix(event.target.value)}
                  placeholder="例如 images/2026/"
                />
              </Field>
              <Field>
                <FieldLabel>搜尋目前清單</FieldLabel>
                <SearchInputWrap>
                  <FaSearch />
                  <TextInput
                    aria-label="搜尋目前清單"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="搜尋檔名或路徑"
                  />
                </SearchInputWrap>
              </Field>
            </FieldGrid>

            {currentFolders.length > 0 && (
              <FolderSection>
                <FolderSectionHeader>
                  <div>
                    <strong>子資料夾</strong>
                    <span>{currentPrefix || "根目錄"} · {currentFolders.length} 個</span>
                  </div>
                </FolderSectionHeader>
              <FolderGrid>
                {currentFolders.map((folder) => {
                  const isDeletingFolder = deletingFolderPrefix === folder.prefix;
                  return (
                    <FolderCard
                      key={folder.prefix}
                      $dropTarget={dragTargetPrefix === normalizePrefix(folder.prefix)}
                      onDragOver={(event) => handleMoveDragOver(event, folder.prefix)}
                      onDragLeave={() => handleMoveDragLeave(folder.prefix)}
                      onDrop={(event) => handleMoveDrop(event, folder.prefix)}
                    >
                      <FolderOpenButton
                        type="button"
                        onClick={() => {
                          setCurrentPrefix(folder.prefix);
                          setUploadPrefix(folder.prefix);
                        }}
                      >
                        <FaFolder />
                        <span>{folder.name}</span>
                        <small>{folder.count} 項</small>
                      </FolderOpenButton>
                      <FolderActions>
                        <SmallButton
                          type="button"
                          onClick={() => downloadFolder(folder.prefix)}
                          disabled={Boolean(downloadingFolderPrefix)}
                        >
                          {downloadingFolderPrefix === folder.prefix ? <SpinningIcon /> : <FaDownload />}
                          {downloadingFolderPrefix === folder.prefix ? "打包中" : "下載"}
                        </SmallButton>
                        <SmallButton
                          type="button"
                          onClick={() => openRenameFolderDialog(folder.prefix)}
                          disabled={renaming || isDeletingFolder}
                        >
                          <FaEdit />
                          編輯
                        </SmallButton>
                        <FolderHoverButton
                          type="button"
                          title="複製資料夾路徑"
                          onClick={() => copyText(folder.prefix, "資料夾路徑已複製")}
                        >
                          <FaCopy />
                        </FolderHoverButton>
                        <DangerSmallButton
                          type="button"
                          onClick={() => deleteFolder(folder.prefix)}
                          disabled={isDeletingFolder}
                        >
                          {isDeletingFolder ? <SpinningIcon /> : <FaTrash />}
                          {isDeletingFolder ? "刪除中" : "刪除"}
                        </DangerSmallButton>
                      </FolderActions>
                    </FolderCard>
                  );
                })}
              </FolderGrid>
              </FolderSection>
            )}

            <DropZone
              $dragOver={dragOver}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={async (event) => {
                event.preventDefault();
                setDragOver(false);
                try {
                  const droppedFiles = await collectDroppedFiles(event.dataTransfer);
                  handleFiles(droppedFiles);
                } catch {
                  showMessage("讀取拖拉資料夾失敗，請改用檔案拖拉。");
                }
              }}
            >
              <FaCloudUploadAlt />
              <strong>選擇、拖拉或貼上檔案 / 資料夾</strong>
              <span>支援多層資料夾，上傳到 {normalizePrefix(uploadPrefix) || "根目錄"}</span>
              <HiddenInput
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(event) => handleFiles(event.target.files)}
              />
            </DropZone>

            <PreviewGrid>
              {files.map((item, index) => (
                <Card key={`${item.file.name}-${index}`}>
                  {item.file.type.startsWith("image/") && (
                    <PreviewImage
                      src={item.url || item.previewUrl}
                      alt=""
                    />
                  )}
                  {!item.file.type.startsWith("image/") && (
                    <FilePreviewIcon>
                      <FaFileAlt />
                    </FilePreviewIcon>
                  )}
                  <FileName>{item.file.name}</FileName>
                  {item.relativePath && item.relativePath !== item.file.name && (
                    <RemoteKey>
                      {normalizeKey(`${normalizePrefix(uploadPrefix)}${item.relativePath}`)}
                    </RemoteKey>
                  )}
                  <Status $status={item.status}>
                    {item.status === "idle" && "等待上傳"}
                    {item.status === "uploading" && "上傳中"}
                    {item.status === "done" && "完成"}
                    {item.status === "error" && "失敗"}
                    {item.status === "cancelled" && "已取消"}
                  </Status>
                  {item.key && <RemoteKey>{item.key}</RemoteKey>}
                  {item.url && (
                    <ButtonRow>
                      <SmallButton type="button" onClick={() => copyText(item.url, "URL 已複製")}>
                        複製 URL
                      </SmallButton>
                    </ButtonRow>
                  )}
                </Card>
              ))}
            </PreviewGrid>

            <Actions>
              {uploading ? (
                <DangerButton type="button" onClick={cancelUpload}>
                  <FaTimes />
                  終止上傳
                </DangerButton>
              ) : (
                <PrimaryButton type="button" onClick={uploadAll} disabled={!files.length}>
                  <FaCloudUploadAlt />
                  開始上傳
                </PrimaryButton>
              )}
              <SecondaryButton type="button" onClick={clearQueuedFiles} disabled={!files.length || uploading}>
                清空待上傳
              </SecondaryButton>
            </Actions>
            {files.length > 0 && (
              <UploadSummary>
                <span>{uploadStats.total} 個待處理</span>
                <span>{uploadStats.done} 完成</span>
                {uploadStats.uploading > 0 && <span>{uploadStats.uploading} 上傳中</span>}
                {uploadStats.error > 0 && <span>{uploadStats.error} 失敗</span>}
                {uploadStats.cancelled > 0 && <span>{uploadStats.cancelled} 已取消</span>}
              </UploadSummary>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>
                <FaFileAlt />
                檔案
              </SectionTitle>
              <Count>{hasSearch ? `${filteredRemoteFiles.length} / ${currentRemoteFiles.length}` : filteredRemoteFiles.length} 個</Count>
            </SectionHeader>

            <DownloadActions>
              <CheckboxLabel>
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleKeys} />
                選取目前列表
              </CheckboxLabel>
              <ActionGroup>
                <SecondaryButton
                  type="button"
                  onClick={() => downloadFolder("", { all: true })}
                  disabled={Boolean(downloadingFolderPrefix) || loadingFiles || !visibleRemoteFiles.length}
                >
                  {downloadingFolderPrefix === "__all__" ? <SpinningIcon /> : <FaDownload />}
                  {downloadingFolderPrefix === "__all__" ? "建立下載中" : "匯出全部 ZIP"}
                </SecondaryButton>
                <SecondaryButton type="button" onClick={downloadSelected} disabled={downloading || !selectedKeys.length}>
                  {downloading ? <SpinningIcon /> : <FaDownload />}
                  {downloading ? "下載中" : `下載 ${selectedKeys.length} 個`}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={openMoveSelectedDialog}
                  disabled={!selectedKeys.length || movingKeys.length > 0}
                >
                  {movingKeys.length > 0 ? <SpinningIcon /> : <FaFolderOpen />}
                  {movingKeys.length > 0 ? "移動中" : "移動選取"}
                </SecondaryButton>
                <DangerButton type="button" onClick={deleteSelected} disabled={!selectedKeys.length || deletingKeys.length > 0}>
                  {deletingKeys.length > 0 ? <SpinningIcon /> : <FaTrash />}
                  {deletingKeys.length > 0 ? "刪除中" : "刪除選取"}
                </DangerButton>
              </ActionGroup>
            </DownloadActions>

            {message && <Message>{message}</Message>}

            <FileList>
              <FileListHeader>
                <span>檔案</span>
                <span>資訊</span>
                <span>操作</span>
              </FileListHeader>
              {loadingFiles && (
                <SkeletonFileList>
                  {Array.from({ length: remoteFiles.length ? 3 : 8 }).map((_, index) => (
                    <SkeletonFileRow key={index}>
                      <SkeletonLine $width="78%" />
                      <SkeletonLine $width="62%" />
                      <SkeletonButtonGroup>
                        <SkeletonPill />
                        <SkeletonPill />
                        <SkeletonPill />
                      </SkeletonButtonGroup>
                    </SkeletonFileRow>
                  ))}
                </SkeletonFileList>
              )}
              {!loadingFiles && filteredRemoteFiles.map((file) => {
                const publicUrl = getPublicUrl(file.key);
                const isDeleting = deletingKeySet.has(file.key);
                const isDownloading = downloadingKeySet.has(file.key);
                const isMoving = movingKeySet.has(file.key);
                return (
                  <FileRow
                    key={file.key}
                    draggable={!isDeleting && !isMoving}
                    $dragging={dragMoveKeys.includes(file.key)}
                    onDragStart={(event) => handleRemoteDragStart(event, file.key)}
                    onDragEnd={handleRemoteDragEnd}
                  >
                    <FileRowLead>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(file.key)}
                        onChange={() => toggleKey(file.key)}
                      />
                      <FileMetaButton
                        type="button"
                        title={file.key}
                        onClick={() => openFilePreview(file)}
                      >
                        <FileMeta>
                        <RemoteFileName>{getFilename(file.key)}</RemoteFileName>
                        <RemoteKey title={file.key}>{file.key}</RemoteKey>
                        </FileMeta>
                      </FileMetaButton>
                    </FileRowLead>
                    <FileInfo>
                      <span>{formatBytes(file.size)}</span>
                      <span>{formatDate(file.uploaded)}</span>
                    </FileInfo>
                    <ButtonRow>
                      <SmallButton type="button" onClick={() => copyText(publicUrl, "URL 已複製")}>
                        <FaCopy />
                        複製 URL
                      </SmallButton>
                      <SmallButton type="button" onClick={() => downloadFile(file.key)} disabled={isDownloading || downloading}>
                        {isDownloading ? <SpinningIcon /> : <FaDownload />}
                        {isDownloading ? "下載中" : "下載"}
                      </SmallButton>
                      <SmallButton
                        type="button"
                        onClick={() => openRenameFileDialog(file.key)}
                        disabled={renaming || isDeleting || isMoving}
                      >
                        <FaEdit />
                        編輯
                      </SmallButton>
                      <DangerSmallButton
                        type="button"
                        onClick={async () => {
                          const result = await confirmDanger({
                            title: "刪除檔案？",
                            text: file.key,
                          });
                          if (!result.isConfirmed) return;
                          try {
                            await deleteKey(file.key);
                            await fetchRemoteFiles();
                            showMessage("檔案已刪除。");
                            swalToast("success", "檔案已刪除");
                          } catch (error) {
                            showMessage(error.message || "刪除失敗");
                            showAssetError({
                              title: "刪除失敗",
                              subject: file.key,
                              message: error.message || "無法刪除檔案。",
                              note: "未成功刪除的檔案仍保留在 Cloudflare。",
                            });
                          }
                        }}
                        disabled={isDeleting || isMoving}
                      >
                        {isDeleting || isMoving ? <SpinningIcon /> : <FaTrash />}
                        {isMoving ? "移動中" : isDeleting ? "刪除中" : "刪除"}
                      </DangerSmallButton>
                    </ButtonRow>
                  </FileRow>
                );
              })}
              {!loadingFiles && !filteredRemoteFiles.length && (
                <EmptyState role="status">
                  {hasSearch && currentRemoteFiles.length > 0 ? <>
                    <FaSearch />
                    <span>搜尋沒有符合的檔案</span>
                    <small>目前位置有 {currentRemoteFiles.length} 個檔案，請調整關鍵字或清除搜尋。</small>
                  </> : <>
                    <FaFolder />
                    <span>目前位置沒有檔案</span>
                  </>}
                  {hasSearch && <SecondaryButton type="button" onClick={() => setSearchTerm("")}>清除搜尋</SecondaryButton>}
                </EmptyState>
              )}
            </FileList>
          </Section>
        </MainPanel>
      </ManagerGrid>

      {copyMessage && <CopyToast>{copyMessage}</CopyToast>}
    </Container>
  );
}

const Container = styled.div`
  max-width: 1180px;
  margin: 32px auto 56px;
  padding: 0 16px;
  color: var(--app-text, #111827);
  --asset-active-bg: #111827;

  [data-auth-theme="night"] & {
    --asset-active-bg: var(--app-nav-active-bg, #4a4a4a);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const TitleGroup = styled.div`
  display: grid;
  gap: 4px;
`;

const Eyebrow = styled.div`
  color: var(--app-muted-text, #6b7280);
  font-size: 13px;
  font-weight: 750;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--app-text, #111827);
  font-size: 28px;
  font-weight: 750;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--app-surface, #fff);

  span {
    display: block;
    color: var(--app-muted-text, #6b7280);
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: var(--app-text, #111827);
    font-size: 18px;
    overflow-wrap: anywhere;
  }
`;

const ManagerGrid = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SidePanel = styled.aside`
  align-self: start;
  display: grid;
  gap: 14px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
`;

const MainPanel = styled.div`
  min-width: 0;
  display: grid;
  gap: 18px;
`;

const Section = styled.section`
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PanelTitle = styled.h3`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--app-text, #111827);
  font-size: 16px;
  font-weight: 750;

  svg {
    color: var(--app-icon-accent, #111827);
    font-size: 14px;
  }
`;

const PanelToggleTitle = styled.button`
  width: 100%;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  border-radius: 6px;
  padding: 0 2px;
  background: transparent;
  color: var(--app-text, #111827);
  font-size: 16px;
  font-weight: 750;
  text-align: left;
  cursor: pointer;

  span {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  svg {
    color: var(--app-icon-accent, #111827);
    font-size: 14px;
    flex: 0 0 auto;
  }

  &:hover,
  &:focus-visible {
    color: var(--app-accent, #2563eb);
    outline: none;
  }
`;

const SectionTitle = styled(PanelTitle)``;

const Count = styled.span`
  color: var(--app-muted-text, #6b7280);
  font-size: 13px;
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const BreadcrumbButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid ${({ $dropTarget }) => ($dropTarget ? "#111827" : "transparent")};
  border-radius: 6px;
  padding: 3px 5px;
  background: ${({ $dropTarget }) =>
    $dropTarget ? "var(--app-surface-soft, #f3f4f6)" : "transparent"};
  color: ${({ $dropTarget }) => ($dropTarget ? "#111827" : "var(--app-muted-text, #4b5563)")};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;

  &:last-child {
    color: var(--app-text, #111827);
  }
`;

const TreeList = styled.div`
  display: grid;
  gap: 4px;
  max-height: 420px;
  overflow: auto;
  border: 1px solid ${({ $dropTarget }) => ($dropTarget ? "#111827" : "transparent")};
  border-radius: 8px;
  padding: 3px;
  background: ${({ $dropTarget }) =>
    $dropTarget ? "var(--app-surface-soft, #f3f4f6)" : "transparent"};
`;

const TreeItem = styled.div`
  min-height: 34px;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px;
  border: 1px solid ${({ $dropTarget }) => ($dropTarget ? "#111827" : "transparent")};
  border-radius: 6px;
  background: ${({ $active, $dropTarget }) =>
    $active ? "var(--asset-active-bg, #111827)" : $dropTarget ? "var(--app-surface-soft, #f3f4f6)" : "transparent"};
  color: ${({ $active }) => ($active ? "#fff" : "var(--app-surface-text, #111827)")};
  transition: background 0.16s ease, border-color 0.16s ease;

  &:hover,
  &:focus-within {
    border-color: ${({ $active }) => ($active ? "var(--asset-active-bg, #111827)" : "var(--app-border, #d1d5db)")};
  }

  &:hover button:last-child,
  &:focus-within button:last-child {
    opacity: 1;
    pointer-events: auto;
  }
`;

const TreeToggleButton = styled.button`
  width: 24px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 5px;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;

  svg {
    font-size: 11px;
  }

  &:hover:not(:disabled) {
    background: ${({ disabled }) =>
      disabled ? "transparent" : "rgba(107, 114, 128, 0.12)"};
  }

  &:disabled {
    cursor: default;
    opacity: 0.28;
  }
`;

const TreeButton = styled.button`
  min-width: 0;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: ${({ $active }) => ($active ? "#e5e7eb" : "var(--app-muted-text, #6b7280)")};
  }
`;

const TreeCopyButton = styled.button`
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: 1px solid ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.36)" : "var(--app-border, #d1d5db)")};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.12)" : "var(--app-input-bg, #fff)")};
  color: inherit;
  opacity: 0;
  pointer-events: none;
  cursor: pointer;
  transition: opacity 0.16s ease;

  svg {
    font-size: 12px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.label`
  color: var(--app-muted-text, #4b5563);
  font-size: 13px;
  font-weight: 700;
`;

const SearchInputWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--app-muted-text, #6b7280);
    font-size: 13px;
    pointer-events: none;
  }

  input {
    padding-left: 32px;
  }
`;

const InlineStack = styled.div`
  display: grid;
  gap: 8px;
`;

const FolderHint = styled.div`
  display: grid;
  gap: 3px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 8px;
  padding: 9px 10px;
  background: var(--app-surface-soft, #f8fafc);

  span {
    color: var(--app-muted-text, #64748b);
    font-size: 12px;
    font-weight: 750;
  }

  strong {
    color: var(--app-text, #111827);
    font-size: 13px;
    overflow-wrap: anywhere;
  }
`;

const HelperText = styled.span`
  color: var(--app-muted-text, #64748b);
  font-size: 12px;
  line-height: 1.45;
`;

const TextInput = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 9px 10px;
  border: 1px solid var(--app-border, #d1d5db);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #111827);
  font: inherit;
`;

const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
`;

const FolderSection = styled.div`
  display: grid;
  gap: 9px;
  margin-bottom: 12px;
`;

const FolderSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  div {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  strong {
    color: var(--app-text, #111827);
    font-size: 14px;
  }

  span {
    color: var(--app-muted-text, #64748b);
    font-size: 12px;
    overflow-wrap: anywhere;
  }
`;

const FolderCard = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;
  border: 1px solid ${({ $dropTarget }) => ($dropTarget ? "#111827" : "var(--app-border, #d8d8d8)")};
  border-radius: 8px;
  padding: 8px 10px;
  background: ${({ $dropTarget }) =>
    $dropTarget ? "var(--app-surface-soft, #f3f4f6)" : "var(--app-input-bg, #fff)"};
  color: var(--app-input-text, #111827);
  transition: border-color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: #9ca3af;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
  }
`;

const FolderOpenButton = styled.button`
  min-width: 0;
  min-height: 32px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;

  svg {
    color: var(--app-icon-accent, #111827);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    font-weight: 750;
  }

  small {
    color: var(--app-muted-text, #6b7280);
  }
`;

const FolderActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const FolderHoverButton = styled.button`
  min-height: 30px;
  width: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid var(--app-border, #d1d5db);
  border-radius: 6px;
  padding: 5px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #111827);
  font-size: 12px;
  font-weight: 700;
  opacity: 0;
  pointer-events: none;
  cursor: pointer;
  transition: opacity 0.16s ease;

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }

  ${FolderCard}:hover &,
  ${FolderCard}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }
`;

const DropZone = styled.div`
  display: grid;
  place-items: center;
  align-content: center;
  gap: 6px;
  min-height: 132px;
  border: 2px dashed ${({ $dragOver }) => ($dragOver ? "#111827" : "var(--app-border, #a3a3a3)")};
  background: ${({ $dragOver }) =>
    $dragOver ? "var(--app-surface-soft, #f3f4f6)" : "var(--app-surface-hover, #fafafa)"};
  border-radius: 8px;
  color: var(--app-muted-text, #525252);
  cursor: pointer;

  svg {
    color: var(--app-icon-accent, #111827);
    font-size: 24px;
  }

  strong {
    color: var(--app-text, #111827);
  }

  span {
    font-size: 13px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const PreviewGrid = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  min-width: 0;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 8px;
  padding: 10px;
  background: var(--app-surface-soft, #fafafa);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: contain;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 8px;
`;

const FilePreviewIcon = styled.div`
  height: 120px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  margin-bottom: 8px;

  svg {
    color: var(--app-muted-text, #6b7280);
    font-size: 32px;
  }
`;

const FileName = styled.div`
  font-size: 13px;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

const Status = styled.div`
  margin-top: 6px;
  color: ${({ $status }) =>
    $status === "done"
      ? "#137333"
      : $status === "error"
        ? "#b3261e"
        : $status === "cancelled"
          ? "#6b7280"
          : "#6b7280"};
  font-size: 12px;
`;

const Actions = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const UploadSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  font-weight: 700;

  span {
    border: 1px solid var(--app-border, #d1d5db);
    border-radius: 6px;
    padding: 5px 8px;
    background: var(--app-input-bg, #fff);
  }
`;

const DownloadActions = styled(Actions)`
  align-items: center;
  justify-content: space-between;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  padding: 9px 14px;
  background: var(--asset-active-bg, #111827);
  color: #fff;
  font-weight: 750;
  cursor: pointer;

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;

    [data-auth-theme="night"] & {
      background: var(--app-border, #555);
    }
  }
`;

const SecondaryButton = styled.button`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid var(--app-border, #d1d5db);
  border-radius: 8px;
  padding: 9px 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #111827);
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(SecondaryButton)`
  border-color: #e4b2ad;
  color: #b3261e;
`;

const SmallButton = styled.button`
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid var(--app-border, #d1d5db);
  border-radius: 6px;
  padding: 5px 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #111827);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const DangerSmallButton = styled(SmallButton)`
  border-color: #e4b2ad;
  color: #b3261e;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`;

const FileRowLead = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`;

const FileList = styled.div`
  margin-top: 12px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 8px;
  overflow: hidden;
`;

const FileListHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px auto;
  gap: 12px;
  padding: 8px 12px;
  background: var(--app-surface-soft, #f8fafc);
  color: var(--app-muted-text, #64748b);
  font-size: 12px;
  font-weight: 750;
  border-bottom: 1px solid var(--app-border, #e5e7eb);

  @media (max-width: 840px) {
    display: none;
  }
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-border, #e5e7eb);
  background: ${({ $dragging }) =>
    $dragging ? "var(--app-surface-soft, #f3f4f6)" : "var(--app-surface, #fff)"};
  opacity: ${({ $dragging }) => ($dragging ? 0.62 : 1)};
  cursor: grab;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const FileMeta = styled.span`
  min-width: 0;
`;

const FileMetaButton = styled.button`
  min-width: 0;
  display: block;
  align-items: center;
  border: 0;
  border-radius: 6px;
  padding: 5px 6px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background: var(--app-surface-soft, #f8fafc);
    outline: none;
  }

  &:hover span:first-child,
  &:focus-visible span:first-child {
    text-decoration: underline;
  }
`;

const RemoteFileName = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 750;
  overflow-wrap: anywhere;
`;

const RemoteKey = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const FileInfo = styled.div`
  display: grid;
  gap: 3px;
  color: var(--app-muted-text, #4b5563);
  font-size: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  min-height: 130px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  color: var(--app-muted-text, #6b7280);
  background: var(--app-surface, #fff);

  svg {
    font-size: 26px;
  }
`;

const Message = styled.div`
  margin-top: 12px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 14px;
`;

const CopyToast = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1200;
  border-radius: 8px;
  padding: 10px 14px;
  background: #111827;
  color: #fff;
  font-weight: 750;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);

  [data-auth-theme="night"] & {
    background: var(--app-surface-soft, #333);
    color: var(--app-text, #eee);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.55;
  }
`;

const SpinningIcon = styled(FaSyncAlt)`
  animation: ${spin} 0.8s linear infinite;
`;

const SkeletonGroup = styled.div`
  display: grid;
  gap: 9px;
  padding: 4px 0;
`;

const SkeletonLine = styled.div`
  width: ${({ $width }) => $width || "100%"};
  height: 14px;
  border-radius: 999px;
  background: var(--app-surface-soft, #eef2f7);
  animation: ${pulse} 1.1s ease-in-out infinite;
`;

const SkeletonFileList = styled.div`
  display: grid;
`;

const SkeletonFileRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px auto;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-bottom: 1px solid var(--app-border, #e5e7eb);

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonButtonGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const SkeletonPill = styled.div`
  width: 58px;
  height: 28px;
  border-radius: 6px;
  background: var(--app-surface-soft, #eef2f7);
  animation: ${pulse} 1.1s ease-in-out infinite;
`;
